import React, { useMemo, useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { RadioButton } from 'primereact/radiobutton';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { useAccounts } from 'hooks/useAccounts';
import {
  reminderOptions,
  adjustmentTypes,
  appliedToItems,
  adjustmentItems,
} from '../../../../billing/CreateInvoice/configs/create_invoice_configs';
import { ModalHeader } from 'shared/ModalHeader';
import { useBatchBillingAggregateQuery } from '../../hooks/queries/useBatchBillingAggregateQuery';
import { useCreateBatchInvoiceMutation } from '../../hooks/mutations/useCreateBatchInvoiceMutation';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

const BatchCreateInvoicesDialog = ({ visible, onHide, onCreate, selectionCount = 0, selectedCaseIds = [], selectedRows = [] }) => {
  const { addToast } = useToast();

  // Time Entry Import Settings
  const [importAllTime, setImportAllTime] = useState(true);
  const [dateRange, setDateRange] = useState(null);

  // Invoice Settings
  const [invoiceDate, setInvoiceDate] = useState(new Date());
  const [dueDate, setDueDate] = useState(null);
  const [paymentReminder, setPaymentReminder] = useState(null);

  // Deposit Settings
  const { accountList } = useAccounts();
  const [depositAccount, setDepositAccount] = useState(null);

  // Global Adjustments
  const [adjustmentItem, setAdjustmentItem] = useState(null);
  const [adjustmentType, setAdjustmentType] = useState(null);
  const [adjustmentValue, setAdjustmentValue] = useState(0);
  const [appliedTo, setAppliedTo] = useState(null);

  // Additional Options
  const [applyTrustFunds, setApplyTrustFunds] = useState(false);
  const [includeUnpaidForward, setIncludeUnpaidForward] = useState(false);

  // Validation errors
  const [validationErrors, setValidationErrors] = useState({
    invoiceDate: '',
    dueDate: '',
  });

  // Fetch batch summary aggregate when dialog is visible and case IDs are available
  const {
    aggregateData,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
  } = useBatchBillingAggregateQuery({
    case_list: selectedCaseIds,
    enabled: visible && selectedCaseIds.length > 0,
  });

  // Calculate summary totals from API aggregate response
  const batchSummary = useMemo(() => {
    return {
      totalUnbilledTime: parseFloat(aggregateData?.uninvoiced_billable_amount || 0),
      totalUnbilledExpenses: parseFloat(aggregateData?.unbilled_expenses_amount || 0),
      totalOutstandingBalances: parseFloat(aggregateData?.total_invoice_due_amount || 0),
      estimatedTotal: parseFloat(aggregateData?.total_unbilled_amount || 0),
      caseCount: aggregateData?.case_count || 0,
    };
  }, [aggregateData]);

  // Invoice Sharing
  const shareOptions = useMemo(
    () => [
      { key: 'INTERNAL', label: 'None - Internal review only' },
      { key: 'BILLING_CONTACT', label: 'Billing Contact only' },
      { key: 'BOTH', label: 'Both Billing Contact and Client' },
    ],
    []
  );
  const [shareWith, setShareWith] = useState('INTERNAL');

  // Mutation hook for batch invoice creation
  const createBatchInvoiceMutation = useCreateBatchInvoiceMutation({
    onSuccess: (data) => {
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Batch invoices created successfully');
      onHide();
      // Optionally call the onCreate callback if needed for additional handling
      if (typeof onCreate === 'function') {
        onCreate(data);
      }
    },
    onError: (error) => {
      console.error('Failed to create batch invoices:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create batch invoices';
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, errorMessage);
    },
  });

  /**
   * Transform form data to API request format
   * Maps dialog state to the batch invoice API schema
   * @param {Array} casesWithDetails - Array of cases with complete contact details
   */
  const transformToApiFormat = (casesWithDetails) => {
    // Build case_contact_info object from detailed case data
    const caseContactInfo = {};

    casesWithDetails.forEach((caseData) => {
      if (caseData.case_uuid && caseData.contact_id) {
        caseContactInfo[caseData.case_uuid] = {
          // Contact Information
          contact_id: caseData.contact_id || '',
          contact_name: caseData.contact_name || '',
          street: caseData.street || caseData.address || '',
          city: caseData.city || '',
          state: caseData.state || '',
          zip_code: caseData.zip_code || caseData.zipcode || '',
          country: caseData.country || 'USA',
          phone: caseData.phone || caseData.phone_number || '',
          country_code: caseData.country_code || '+1',
          // Case-specific Information
          case_name: caseData.case_name || '',
          payment_structure: caseData.payment_structure || '',
          created_by_firm_user_first_name: caseData.created_by_firm_user_first_name || '',
          created_by_firm_user_last_name: caseData.created_by_firm_user_last_name || '',
          created_by_firm_user_id: caseData.created_by_firm_user_id || caseData.created_by_user_id || '',
        };
      }
    });

    // Map invoice sharing to API format
    let invoiceSharingArray = [];
    switch (shareWith) {
      case 'BILLING_CONTACT':
        invoiceSharingArray = ['billing_contact'];
        break;
      case 'BOTH':
        invoiceSharingArray = ['main_client', 'billing_contact'];
        break;
      case 'INTERNAL':
      default:
        invoiceSharingArray = [];
        break;
    }

    // Get selected account details
    const selectedAccount = accountList?.find((acc) => acc.account_id === depositAccount);

    // Get adjustment item and applied to names
    const selectedAdjustmentItem = adjustmentItems?.find((item) => item.adjustment_item_id === adjustmentItem);
    const selectedAppliedTo = appliedToItems?.find((item) => item.adjustment_to_id === appliedTo);
    const selectedAdjustmentType = adjustmentTypes?.find((type) => type.adjustment_type_id === adjustmentType);

    // Build reminder array if payment reminder is set
    const reminderArray = paymentReminder
      ? [
          {
            reminder_expression: reminderOptions?.find((opt) => opt.value === paymentReminder)?.label || '3 days before due date',
            reminder_channel_id: 'email-channel-001', // Default email channel
            reminder_channel_type: 'email',
          },
        ]
      : [];

    // Build the API request payload
    const apiPayload = {
      case_contact_info: caseContactInfo,
      invoice_date: invoiceDate ? new Date(invoiceDate).toISOString() : new Date().toISOString(),
      invoice_due_date: dueDate ? new Date(dueDate).toISOString() : null,
      time_entry_start_date: importAllTime || !dateRange?.[0] ? '1970-01-01T00:00:00Z' : new Date(dateRange[0]).toISOString(),
      time_entry_end_date: importAllTime || !dateRange?.[1] ? new Date().toISOString() : new Date(dateRange[1]).toISOString(),
      invoice_sharing: invoiceSharingArray,
      adjustment_type: selectedAdjustmentType?.adjustment_type?.toLowerCase() || null,
      adjustment_value: adjustmentValue || 0,
      adjustment_applied_to: selectedAppliedTo?.adjustment_to || null,
      adjustment_item: selectedAdjustmentItem?.adjustment_item || null,
      deposit_account_id: selectedAccount?.account_id || depositAccount || null,
      deposit_account_name: selectedAccount?.account_name || 'Main Operating Account',
      deposit_account_type: selectedAccount?.account_type || 'operating',
      apply_available_trust_funds: applyTrustFunds,
      include_unpaid_invoice_balance_forward: includeUnpaidForward,
      is_reminder: !!paymentReminder,
      reminder: reminderArray,
    };

    // Remove null/undefined values for cleaner payload
    Object.keys(apiPayload).forEach((key) => {
      if (apiPayload[key] === null || apiPayload[key] === undefined) {
        delete apiPayload[key];
      }
    });

    return apiPayload;
  };

  const handleCreateInvoices = () => {
    // Reset validation errors
    const errors = {
      invoiceDate: '',
      dueDate: '',
    };

    // Validate required fields
    let hasErrors = false;

    if (selectedRows.length === 0) {
      addToast(false, toastConstant.toasterType.WARNING, 'Warning', 'Please select at least one case');
      return;
    }

    if (!invoiceDate) {
      errors.invoiceDate = 'Invoice date is required';
      hasErrors = true;
    }

    if (!dueDate) {
      errors.dueDate = 'Due date is required';
      hasErrors = true;
    }

    // Update validation errors state
    setValidationErrors(errors);

    // If there are errors, don't proceed
    if (hasErrors) {
      return;
    }

    // Transform data with selectedRows (which already contains all case details from batch billing list)
    const apiPayload = transformToApiFormat(selectedRows);
    createBatchInvoiceMutation.mutate(apiPayload);
  };

  const disabledForRange = !importAllTime;

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      className="add-contact-dialog create-msg-popup"
      header={ModalHeader}
      closable={false}
      style={{ width: '60rem' }}
    >
      <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
        <div className="modal-heading F-size16 text-bold">Create Batch Invoices</div>
      </div>
      <div className="row g-3">
        <div className="col-12">
          <div className="p-medium mb-2 bb-subheading">Time Entry Import Settings</div>
          <div className="d-flex align-items-center flex-wrap">
            <div className="d-flex align-items-center me-4 mb-2">
              <RadioButton inputId="bb_all_time" checked={importAllTime} onChange={() => setImportAllTime(true)} />
              <label htmlFor="bb_all_time" className="ms-2">
                Import outstanding entries from all time
              </label>
            </div>
            <div className="d-flex align-items-center mb-2">
              <RadioButton inputId="bb_date_range" checked={!importAllTime} onChange={() => setImportAllTime(false)} />
              <label htmlFor="bb_date_range" className="ms-2 me-2">
                Import entries from date range
              </label>
              <Calendar
                selectionMode="range"
                value={dateRange}
                onChange={(e) => setDateRange(e.value)}
                disabled={importAllTime}
                className="input-shadow"
                showIcon
              />
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="p-medium mb-2 bb-subheading">Invoice Settings</div>
          <div className="row g-3">
            <div className="col-12 col-lg-4">
              <div className="p-medium mb-1">
                Invoice Date (for all invoices) <span style={{ color: 'red' }}>*</span>
              </div>
              <Calendar
                value={invoiceDate}
                onChange={(e) => {
                  setInvoiceDate(e.value);
                  // Clear error when user selects a date
                  if (e.value && validationErrors.invoiceDate) {
                    setValidationErrors((prev) => ({ ...prev, invoiceDate: '' }));
                  }
                }}
                placeholder="Select invoice date"
                className={`input-shadow w-100 ${validationErrors.invoiceDate ? 'p-invalid' : ''}`}
                showIcon
              />
              {validationErrors.invoiceDate && (
                <small className="p-error" style={{ display: 'block', marginTop: '0.25rem' }}>
                  {validationErrors.invoiceDate}
                </small>
              )}
            </div>
            <div className="col-12 col-lg-4">
              <div className="p-medium mb-1">
                Due Date (for all invoices) <span style={{ color: 'red' }}>*</span>
              </div>
              <Calendar
                value={dueDate}
                onChange={(e) => {
                  setDueDate(e.value);
                  // Clear error when user selects a date
                  if (e.value && validationErrors.dueDate) {
                    setValidationErrors((prev) => ({ ...prev, dueDate: '' }));
                  }
                }}
                placeholder="Select due date"
                className={`input-shadow w-100 ${validationErrors.dueDate ? 'p-invalid' : ''}`}
                showIcon
              />
              {validationErrors.dueDate && (
                <small className="p-error" style={{ display: 'block', marginTop: '0.25rem' }}>
                  {validationErrors.dueDate}
                </small>
              )}
            </div>
            <div className="col-12 col-lg-4">
              <div className="p-medium mb-1">Payment Reminder</div>
              <Dropdown
                value={paymentReminder}
                options={reminderOptions}
                onChange={(e) => setPaymentReminder(e.value)}
                optionLabel="label"
                optionValue="value"
                placeholder="No Reminder"
                className="input-shadow w-100"
                showClear
              />
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="p-medium mb-2 bb-subheading">Deposit Settings</div>
          <div className="row g-3">
            <div className="col-12 col-lg-6">
              <div className="p-medium mb-1">Deposit Account</div>
              <Dropdown
                value={depositAccount}
                options={accountList || []}
                optionLabel="account_name"
                optionValue="account_id"
                onChange={(e) => setDepositAccount(e.value)}
                placeholder="Select account"
                className="input-shadow w-100"
                showClear
              />
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="p-medium mb-2 bb-subheading">Global Adjustments (Optional)</div>
          <div className="row g-3">
            <div className="col-12 col-lg-3">
              <div className="p-medium mb-1">Adjustment Item</div>
              <Dropdown
                value={adjustmentItem}
                options={adjustmentItems || []}
                optionLabel="adjustment_item"
                optionValue="adjustment_item_id"
                onChange={(e) => setAdjustmentItem(e.value)}
                placeholder="Select adjustment"
                className="input-shadow w-100"
                showClear
              />
            </div>
            <div className="col-12 col-lg-3">
              <div className="p-medium mb-1">Adjustment Type</div>
              <Dropdown
                value={adjustmentType}
                options={adjustmentTypes || []}
                optionLabel="adjustment_type"
                optionValue="adjustment_type_id"
                onChange={(e) => setAdjustmentType(e.value)}
                placeholder="Select type"
                className="input-shadow w-100"
                showClear
              />
            </div>
            <div className="col-12 col-lg-3">
              <div className="p-medium mb-1">Adjustment Value</div>
              <InputNumber
                value={adjustmentValue}
                onValueChange={(e) => setAdjustmentValue(e.value || 0)}
                className="input-shadow w-100"
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={2}
                placeholder="Enter value"
              />
            </div>
            <div className="col-12 col-lg-3">
              <div className="p-medium mb-1">Applied To</div>
              <Dropdown
                value={appliedTo}
                options={appliedToItems || []}
                optionLabel="adjustment_to"
                optionValue="adjustment_to_id"
                onChange={(e) => setAppliedTo(e.value)}
                placeholder="Select application"
                className="input-shadow w-100"
                showClear
              />
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="p-medium mb-2 bb-subheading">Additional Options</div>
          <div className="d-flex align-items-center flex-wrap">
            <div className="d-flex align-items-center me-4 mb-2">
              <InputSwitch checked={applyTrustFunds} onChange={(e) => setApplyTrustFunds(e.value)} />
              <span className="ms-2">Apply Available Trust Funds</span>
            </div>
            <div className="d-flex align-items-center mb-2">
              <InputSwitch checked={includeUnpaidForward} onChange={(e) => setIncludeUnpaidForward(e.value)} />
              <span className="ms-2">Include Unpaid Invoice Balance Forward</span>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="p-medium mb-2 bb-subheading">Invoice Sharing</div>
          <div className="d-flex align-items-center flex-wrap">
            {shareOptions.map((o) => (
              <div key={o.key} className="d-flex align-items-center me-4 mb-2">
                <RadioButton
                  inputId={`share_${o.key}`}
                  value={o.key}
                  checked={shareWith === o.key}
                  onChange={(e) => setShareWith(e.value)}
                />
                <label htmlFor={`share_${o.key}`} className="ms-2">
                  {o.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="col-12">
          <div className="bg-white shadow-middle p-3">
            <div className="p-medium mb-2 bb-subheading">Batch Summary</div>
            {isSummaryLoading ? (
              <div className="text-muted">Loading summary...</div>
            ) : isSummaryError ? (
              <div className="text-danger">Error loading summary. Please try again.</div>
            ) : (
              <>
                <div className="text-muted">Selected Cases: {batchSummary?.caseCount || selectionCount}</div>
                <div className="text-muted">Total Unbilled Time: ${(batchSummary?.totalUnbilledTime || 0).toFixed(2)}</div>
                <div className="text-muted">Total Unbilled Expenses: ${(batchSummary?.totalUnbilledExpenses || 0).toFixed(2)}</div>
                <div className="text-muted">Total Outstanding Balances: ${(batchSummary?.totalOutstandingBalances || 0).toFixed(2)}</div>
                <div className="text-muted mt-2">
                  <strong>Estimated Total: ${(batchSummary?.estimatedTotal || 0).toFixed(2)}</strong>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="footer-btn">
        <Button
          className="p-button-secondary outline mx-2"
          label="Cancel"
          onClick={onHide}
          disabled={createBatchInvoiceMutation.isPending || createBatchInvoiceMutation.isLoading}
        />
        <Button
          className="p-button p-button-secondary"
          label={createBatchInvoiceMutation.isPending || createBatchInvoiceMutation.isLoading ? 'Creating...' : 'Create Invoices'}
          onClick={handleCreateInvoices}
          loading={createBatchInvoiceMutation.isPending || createBatchInvoiceMutation.isLoading}
          disabled={createBatchInvoiceMutation.isPending || createBatchInvoiceMutation.isLoading || selectionCount === 0}
        />
      </div>
    </Dialog>
  );
};

export default BatchCreateInvoicesDialog;
